import { useState, useEffect } from "react";
import { Share2, Copy, Users, Award, Gift, CheckCircle, Loader2, Crown, TrendingUp, Zap, Target, MessageSquare, Clock, X, ChevronDown, ChevronUp, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Intro Requests Panel ───────────────────────────────────────────────
const STATUS_CONFIG = {
  pending:  { label: "Pending",  color: "#FF9F0A", bg: "bg-[#FF9F0A]/10 border-[#FF9F0A]/20" },
  accepted: { label: "Accepted", color: "#00FF88", bg: "bg-[#00FF88]/10 border-[#00FF88]/20" },
  declined: { label: "Declined", color: "#FF3B30", bg: "bg-[#FF3B30]/10 border-[#FF3B30]/20" },
};

function MyIntrosPanel() {
  const [intros, setIntros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API}/api/network/intro-requests`, { credentials: "include" })
      .then(r => r.json())
      .then(d => setIntros(d.intro_requests || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const pending  = intros.filter(i => i.status === "pending").length;
  const accepted = intros.filter(i => i.status === "accepted").length;

  return (
    <div className="bg-[#111]/60 border border-white/[0.06] rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors"
        data-testid="my-intros-toggle"
      >
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[#D4AF37]" />
          <span className="text-white font-semibold text-sm">My Intro Requests</span>
          {pending > 0 && (
            <span className="px-1.5 py-0.5 bg-[#FF9F0A]/20 text-[#FF9F0A] text-[10px] font-bold rounded-full">
              {pending} pending
            </span>
          )}
          {accepted > 0 && (
            <span className="px-1.5 py-0.5 bg-[#00FF88]/20 text-[#00FF88] text-[10px] font-bold rounded-full">
              {accepted} accepted
            </span>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
      </button>

      {expanded && (
        <div className="border-t border-white/[0.06] p-4 space-y-2 max-h-80 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-4 h-4 text-[#D4AF37] animate-spin" />
            </div>
          ) : intros.length === 0 ? (
            <div className="text-center py-6 space-y-2">
              <MessageSquare className="w-8 h-8 text-white/10 mx-auto" />
              <p className="text-white/30 text-xs">No intro requests yet</p>
              <p className="text-white/15 text-[10px]">Browse the Directory and click "Request Intro" to get connected</p>
            </div>
          ) : (
            intros.map((intro) => {
              const cfg = STATUS_CONFIG[intro.status] || STATUS_CONFIG.pending;
              const timeAgo = intro.created_at
                ? new Date(intro.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                : "";
              return (
                <div key={intro.intro_id} className="flex items-start gap-3 p-3 bg-white/[0.02] rounded-lg border border-white/[0.04]"
                  data-testid={`intro-item-${intro.intro_id}`}>
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5"
                    style={{ background: `${cfg.color}15`, color: cfg.color }}
                  >
                    {intro.pro_name?.charAt(0) || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white text-xs font-semibold truncate">{intro.pro_name}</p>
                      <span className="text-[9px] text-white/30">{intro.pro_role}</span>
                    </div>
                    {intro.message && (
                      <p className="text-white/30 text-[10px] mt-0.5 line-clamp-1 italic">"{intro.message}"</p>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[9px] px-2 py-0.5 rounded-full border font-semibold ${cfg.bg}`}
                        style={{ color: cfg.color }}>
                        {cfg.label}
                      </span>
                      {timeAgo && (
                        <span className="text-[9px] text-white/20 flex items-center gap-0.5">
                          <Clock className="w-2.5 h-2.5" />{timeAgo}
                        </span>
                      )}
                      {intro.status === "accepted" && intro.pro_email && (
                        <button
                          onClick={() => navigate(`/messages?to=${encodeURIComponent(intro.pro_email)}&name=${encodeURIComponent(intro.pro_name)}`)}
                          className="flex items-center gap-1 px-2 py-0.5 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 text-[#D4AF37] text-[9px] font-bold rounded transition-all"
                          data-testid={`message-intro-${intro.intro_id}`}
                        >
                          <MessageCircle className="w-2.5 h-2.5" /> Message
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

const MILESTONES = [
  { count: 1,  label: "First Referral",    reward: "100 bonus credits",     color: "#00FF88" },
  { count: 5,  label: "Network Builder",   reward: "Priority support badge", color: "#0A84FF" },
  { count: 10, label: "Deal Connector",    reward: "Featured in directory",  color: "#D4AF37" },
  { count: 25, label: "Network Champion",  reward: "Unlock advanced analytics", color: "#AF52DE" },
];

function MilestoneTracker({ total }) {
  return (
    <div className="bg-[#111]/60 border border-white/[0.06] rounded-xl p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Target className="w-4 h-4 text-[#D4AF37]" />
        <h3 className="text-white font-semibold text-sm">Referral Milestones</h3>
      </div>
      <div className="space-y-3">
        {MILESTONES.map((m) => {
          const reached = total >= m.count;
          const pct = Math.min(100, Math.round((total / m.count) * 100));
          return (
            <div key={m.count} className={`p-3 rounded-lg border transition-all ${
              reached ? "border-[#00FF88]/20 bg-[#00FF88]/[0.04]" : "border-white/[0.05] bg-white/[0.02]"
            }`}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  {reached
                    ? <CheckCircle className="w-4 h-4 text-[#00FF88]" />
                    : <div className="w-4 h-4 rounded-full border border-white/20" />
                  }
                  <span className={`text-xs font-semibold ${reached ? "text-white" : "text-white/50"}`}>{m.label}</span>
                  <span className="text-[10px] text-white/20">{m.count} referral{m.count > 1 ? "s" : ""}</span>
                </div>
                <span className="text-[10px] font-semibold" style={{ color: reached ? "#00FF88" : m.color }}>
                  {m.reward}
                </span>
              </div>
              {!reached && (
                <div className="h-1 bg-white/[0.06] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, background: m.color }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GrowthMetrics() {
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/network/growth-metrics`, { credentials: "include" })
      .then(r => r.json())
      .then(setMetrics)
      .catch(() => {});
  }, []);

  if (!metrics) return null;

  return (
    <div className="bg-[#111]/60 border border-white/[0.06] rounded-xl p-5 space-y-4">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-[#D4AF37]" />
        <h3 className="text-white font-semibold text-sm">Network Health</h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { label: "Total Members", value: metrics.total_members, color: "#D4AF37" },
          { label: "New (30d)",     value: metrics.new_members_30d, color: "#00FF88" },
          { label: "Live Deals",    value: metrics.live_deals, color: "#0A84FF" },
          { label: "Feed Posts (7d)", value: metrics.feed_posts_7d, color: "#AF52DE" },
          { label: "Lenders",       value: metrics.by_role?.lenders, color: "#0A84FF" },
          { label: "Contractors",   value: metrics.by_role?.contractors, color: "#FF9F0A" },
        ].map(m => (
          <div key={m.label} className="text-center p-3 bg-white/[0.02] rounded-lg border border-white/[0.04]">
            <p className="font-bold text-lg" style={{ color: m.color }}>{m.value ?? "—"}</p>
            <p className="text-[10px] text-white/30 mt-0.5">{m.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ReferralsTab({ user }) {
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, activityRes, leaderRes] = await Promise.all([
        fetch(`${API}/api/network/referral/stats`, { credentials: "include" }),
        fetch(`${API}/api/network/referral/activity`, { credentials: "include" }),
        fetch(`${API}/api/network/referral/leaderboard`, { credentials: "include" }),
        // Fire milestone check silently — grants credits if newly earned
        fetch(`${API}/api/network/milestone-check`, { method: "POST", credentials: "include" }).catch(() => {}),
      ]);
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      if (activityRes.ok) {
        const data = await activityRes.json();
        setActivities(data.activities || []);
      }
      if (leaderRes.ok) {
        const data = await leaderRes.json();
        setLeaderboard(data.leaderboard || []);
      }
    } catch (err) {
      console.error("Failed to fetch referral data:", err);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!stats?.invite_link) return;
    try {
      await navigator.clipboard.writeText(stats.invite_link);
      setCopied(true);
      toast.success("Invite link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const shareLink = async () => {
    if (!stats?.invite_link) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join me on DealForge",
          text: "Join DealForge - The Operating System for Real Estate Operators",
          url: stats.invite_link
        });
      } catch {}
    } else {
      copyLink();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/20 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div className="text-xs text-white/50 uppercase tracking-wider">Total Referrals</div>
          </div>
          <div className="text-3xl font-bold text-white">{stats?.total_referrals || 0}</div>
        </div>
        
        <div className="bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Award className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="text-xs text-white/50 uppercase tracking-wider">Credits Earned</div>
          </div>
          <div className="text-3xl font-bold text-emerald-400">{stats?.total_credits || 0}</div>
        </div>
        
        <div className="bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Gift className="w-5 h-5 text-purple-400" />
            </div>
            <div className="text-xs text-white/50 uppercase tracking-wider">Per Referral</div>
          </div>
          <div className="text-3xl font-bold text-purple-400">+{stats?.credits_per_referral || 100}</div>
        </div>
      </div>

      {/* Invite Link Section */}
      <div className="bg-[#111]/60 border border-white/[0.06] rounded-xl p-6">
        <h3 className="text-white font-semibold mb-1">Your Invite Link</h3>
        <p className="text-white/40 text-sm mb-4">Share this link to earn credits when friends join DealForge</p>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-3 font-mono text-sm text-[#D4AF37] truncate">
            {stats?.invite_link || "Loading..."}
          </div>
          <div className="flex gap-2">
            <button
              onClick={copyLink}
              data-testid="copy-referral-link"
              className="flex items-center gap-2 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white text-sm font-medium transition-all"
            >
              {copied ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied" : "Copy"}
            </button>
            <button
              onClick={shareLink}
              data-testid="share-referral-link"
              className="flex items-center gap-2 px-4 py-3 bg-[#D4AF37] hover:bg-[#c4a030] text-black rounded-lg text-sm font-bold transition-all"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
          <p className="text-emerald-400 text-sm">
            <strong>Earn 100 credits</strong> for every friend who joins using your link!
          </p>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Referrals */}
        <div className="bg-[#111]/60 border border-white/[0.06] rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-[#D4AF37]" />
            Recent Referrals
          </h3>
          
          {activities.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-10 h-10 text-white/10 mx-auto mb-3" />
              <p className="text-white/30 text-sm">No referrals yet</p>
              <p className="text-white/20 text-xs mt-1">Share your link to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activities.slice(0, 5).map((activity, i) => (
                <div key={activity.referral_id || i} className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37]/30 to-[#D4AF37]/10 flex items-center justify-center text-xs font-bold text-[#D4AF37]">
                      {activity.referred_name?.charAt(0) || "?"}
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{activity.referred_name}</p>
                      <p className="text-white/30 text-xs">
                        {new Date(activity.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <span className="text-emerald-400 text-sm font-semibold">+{activity.credits_awarded}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Leaderboard */}
        <div className="bg-[#111]/60 border border-white/[0.06] rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Crown className="w-4 h-4 text-[#D4AF37]" />
            Top Referrers
          </h3>
          
          {leaderboard.length === 0 ? (
            <div className="text-center py-8">
              <Crown className="w-10 h-10 text-white/10 mx-auto mb-3" />
              <p className="text-white/30 text-sm">No leaders yet</p>
              <p className="text-white/20 text-xs mt-1">Be the first!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {leaderboard.slice(0, 5).map((entry) => (
                <div
                  key={entry.user_id}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    entry.is_current_user ? "bg-[#D4AF37]/10 border border-[#D4AF37]/20" : "bg-white/[0.02]"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      entry.rank === 1 ? "bg-[#D4AF37] text-black" :
                      entry.rank === 2 ? "bg-gray-400 text-black" :
                      entry.rank === 3 ? "bg-amber-700 text-white" :
                      "bg-white/10 text-white/60"
                    }`}>
                      {entry.rank}
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${entry.is_current_user ? "text-[#D4AF37]" : "text-white"}`}>
                        {entry.name} {entry.is_current_user && "(You)"}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white text-sm font-semibold">{entry.referral_count}</p>
                    <p className="text-white/30 text-xs">referrals</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Referral Code */}
      <div className="text-center py-4">
        <p className="text-white/30 text-xs">
          Your referral code: <span className="text-[#D4AF37] font-mono font-bold">{stats?.referral_code}</span>
        </p>
      </div>

      {/* My Intro Requests */}
      <MyIntrosPanel />

      {/* Milestones */}
      <MilestoneTracker total={stats?.total_referrals || 0} />

      {/* Network Health */}
      <GrowthMetrics />
    </div>
  );
}

export default ReferralsTab;
